import { __assign } from "tslib";
import { Trash16, UserLock16, ViewAdd24 } from '@yunex/yds-icons';
import { Box, Button, colors, Dialog, DialogActions, DialogContent, DialogTitle, Fade, GRID_ROW_HEIGHT, SwitchCellRenderer, Tooltip, } from '@yunex/yds-react';
import { AgGridReact } from 'ag-grid-react';
import React, { memo, useCallback, useMemo, useRef, useState, } from 'react';
import { t } from '../../../../localization/getTranslation';
import { CMP_EDIT_VIEWS_DIALOG as CMP } from '../../../componentNames';
import LoadingOrEmptyState from '../LoadingOrEmptyState';
import { CenteredBox } from './CenteredBox';
import { EditableCellRenderer } from './EditableCellRenderer';
import { EditViewsActionsCellRenderer } from './EditViewsActionsCellRenderer';
import { RowDragCellRenderer } from './RowDragCellRenderer';
var GRID_SPACER = 24;
// Styles
var styles = {
    gridWrapper: function (rowCount) { return ({
        height: rowCount * GRID_ROW_HEIGHT + GRID_SPACER,
        minHeight: 2 * GRID_ROW_HEIGHT + GRID_SPACER,
        maxHeight: 7 * GRID_ROW_HEIGHT + GRID_SPACER,
        width: '100%',
        '& .ag-drag-handle.ag-row-drag': {
            visibility: 'hidden !important',
            width: '0px',
            margin: '0',
        },
        '& .ag-row': {
            borderStyle: 'none !important',
        },
        '& .ag-row.ag-row-focus': {
            backgroundColor: 'transparent',
        },
        '& .ag-header': {
            display: 'none',
        },
        '& .switch-renderer .MuiSwitch-root': {
            margin: '0 0 0 2px',
        },
        '& .ag-row-animation .ag-row': {
            transition: 'transform 0.2s, top 0.2s, background-color 0.1s, opacity 0.15s',
        },
        '& [col-id="rowDrag"] > div': {
            height: '100%',
        },
    }); },
};
var EditViewsDialog = memo(function (_a) {
    var cancelButtonLabel = _a.cancelButtonLabel, dialogProps = _a.dialogProps, dialogTitle = _a.dialogTitle, isAdmin = _a.isAdmin, onClose = _a.onClose, onSave = _a.onSave, open = _a.open, saveButtonLabel = _a.saveButtonLabel, _b = _a.savedViewItems, savedViewItems = _b === void 0 ? [] : _b;
    var _c = useState(false), areDataInvalid = _c[0], setAreDataInvalid = _c[1];
    var _d = useState(false), fadeIn = _d[0], setFadeIn = _d[1];
    var _e = useState(), gridApi = _e[0], setGridApi = _e[1];
    var wrapper = useRef(null);
    // New object has to be made, since ag-grid would update savedViewItems directly
    var rowDataMemo = useMemo(function () {
        return savedViewItems.map(function (item) { return ({
            isActive: item.isActive,
            caption: item.caption,
            icon: item.icon,
            isForAllUsers: item.isForAllUsers,
            isSetByAdmin: item.isSetByAdmin,
            id: item.id,
        }); });
    }, [savedViewItems]);
    var _f = useState(rowDataMemo), rowData = _f[0], setRowData = _f[1];
    var initRowCount = useRef(rowDataMemo.length);
    var handleSubmit = function () {
        var result = [];
        gridApi === null || gridApi === void 0 ? void 0 : gridApi.forEachNode(function (_a) {
            var data = _a.data;
            if (!data) {
                return;
            }
            if (!data.id) {
                console.warn('YDS-REACT-COMPOSITES: Missing id in saved view item. Returned data can be invalid.');
            }
            var foundView = savedViewItems.find(function (savedViewItem) { return savedViewItem.id === data.id; });
            var resultBase = {
                isActive: data.isActive,
                caption: data.caption,
                isForAllUsers: data.isForAllUsers,
            };
            if (foundView) {
                result.push(__assign(__assign({}, foundView), resultBase));
            }
            else {
                // Default view is created.
                result.push(__assign(__assign({}, resultBase), { savedViewDataModel: { filters: {}, key: 'NOT_DEFINED' } }));
            }
        });
        onSave(result);
    };
    var onGridReady = useCallback(function (_a) {
        var _b;
        var api = _a.api;
        // These tab guards prevents focus traversing through the modal
        Array.from((_b = wrapper === null || wrapper === void 0 ? void 0 : wrapper.current) === null || _b === void 0 ? void 0 : _b.getElementsByClassName('ag-tab-guard')).forEach(function (node) { return node.remove(); });
        setGridApi(api);
    }, [wrapper === null || wrapper === void 0 ? void 0 : wrapper.current]);
    var columnDefs = useMemo(function () {
        return [
            {
                field: 'rowDrag',
                rowDrag: true,
                minWidth: 22,
                width: 22,
                cellRenderer: RowDragCellRenderer,
            },
            {
                field: 'isActive',
                cellRenderer: SwitchCellRenderer,
                cellRendererParams: function (params) { return ({
                    onChange: function (event) {
                        params.node.setDataValue('isActive', event.target.checked);
                    },
                }); },
                width: 41,
            },
            {
                field: 'icon',
                cellRenderer: function (params) { return (React.createElement(CenteredBox, null, params.value)); },
                width: 20,
                maxWidth: 20,
            },
            {
                field: 'caption',
                cellRenderer: EditableCellRenderer,
                cellStyle: { marginLeft: '-1px', width: '385px' },
                flex: 1,
            },
            {
                hide: !isAdmin,
                field: 'isForAllUsers',
                width: 82,
                cellRenderer: EditViewsActionsCellRenderer,
                // pinned: 'right',
            },
            {
                // pinned: 'right',
                hide: isAdmin,
                field: 'isSetByAdmin',
                width: 36,
                // value represents the boolean value of isSetByAdmin
                cellRenderer: function (params) {
                    return params.value ? (React.createElement(CenteredBox, null,
                        React.createElement(UserLock16, { color: colors.gray20 }))) : (React.createElement(CenteredBox, null,
                        React.createElement(Tooltip, { placement: "left", title: t('delete') },
                            React.createElement(Button, { "aria-label": t('delete'), color: "tertiary", 
                                // data-testid={`${CMP}-${params.node.key}-button`} // TODO ok use params.node.key?
                                icon: true, onClick: function () {
                                    setRowData(rowData.filter(function (row) { return row.id !== params.node.data.id; }));
                                } },
                                React.createElement(Trash16, null)))));
                },
            },
        ];
    }, [isAdmin]);
    var defaultColDef = useMemo(function () {
        var keysToSuppress = [
            'ArrowLeft',
            'ArrowUp',
            'ArrowRight',
            'ArrowDown',
            'Tab',
            'Home',
            'End',
        ];
        return {
            headerComponent: function () { return null; },
            cellStyle: { padding: '0', textAlign: 'center' },
            filter: false,
            resizable: false,
            sortable: false,
            suppressHeaderKeyboardEvent: function () { return true; },
            suppressKeyboardEvent: function (params) {
                return keysToSuppress.includes(params.event.key);
            },
        };
    }, []);
    var createDefaultView = useCallback(function () {
        var newView = {
            id: 'NOT_SET',
            isActive: true,
            caption: t('newViewLabel'),
            isForAllUsers: false,
        };
        setRowData([newView]);
        gridApi === null || gridApi === void 0 ? void 0 : gridApi.setRowData([newView]);
    }, [gridApi, t]);
    return (React.createElement(Dialog, __assign({ "aria-labelledby": "dialog-title", "data-testid": "".concat(CMP, "-root"), fullWidth: true, maxWidth: "sm", onClose: onClose, open: open }, dialogProps),
        React.createElement(DialogTitle, { filled: true, onClose: onClose }, dialogTitle),
        React.createElement(DialogContent, null,
            React.createElement(Fade, { in: fadeIn, timeout: 100 },
                React.createElement(Box, { className: "ag-theme-alpine", display: rowData.length ? 'block' : 'none', ref: wrapper, sx: styles.gridWrapper(initRowCount.current) },
                    React.createElement(AgGridReact, { animateRows: true, columnDefs: columnDefs, context: {
                            isAdmin: isAdmin,
                            rowData: rowData,
                            setRowData: setRowData,
                            setAreDataInvalid: setAreDataInvalid,
                        }, defaultColDef: defaultColDef, getRowId: function (params) { return params.data.id; }, headerHeight: 0, onColumnResized: function () { return setFadeIn(true); }, onGridReady: onGridReady, onRowDragEnd: function (e) {
                            // Wait for animation end, then reorder the rows for keyboard navigation to work properly
                            setTimeout(function () {
                                e.api.redrawRows();
                            }, 300);
                        }, rowBuffer: 0, rowData: rowData || [], rowDragManaged: true, rowHeight: GRID_ROW_HEIGHT + 3, suppressRowClickSelection: true, suppressRowHoverHighlight: true }))),
            rowData.length === 0 && (React.createElement(LoadingOrEmptyState, { action: React.createElement(Button, { color: "secondary", onClick: createDefaultView }, t('addDefaultView')), icon: React.createElement(ViewAdd24, null), sx: { py: 1 }, title: t('noViews') }))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { color: "primary", "data-testid": "".concat(CMP, "-submit-button"), disabled: areDataInvalid, onClick: handleSubmit }, saveButtonLabel || t('saveLabel')),
            React.createElement(Button, { color: "secondary", "data-testid": "".concat(CMP, "-cancel-button"), onClick: onClose }, cancelButtonLabel))));
});
EditViewsDialog.displayName = CMP;
export default EditViewsDialog;
//# sourceMappingURL=EditViewsDialog.js.map